import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { useBoolean } from "usehooks-ts";

import {
  PASSWORD_MIN_LENGTH,
  hasLowercase,
  hasNumber,
  hasUppercase,
} from "../helpers/password-errors";

import { PasswordRequirementTooltip } from "components/sso/PasswordRequirementTooltip";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";

export type PasswordFormValue = {
  password: string;
  confirm_password: string;
};

interface PasswordFormProps {
  currentPassword?: string;
  useEmailLoginFormStyling?: boolean;
  isNewPassword?: boolean;
  eventPrefix?: string;
}

export const PasswordForm: FC<PasswordFormProps> = ({
  currentPassword,
  useEmailLoginFormStyling,
  isNewPassword,
  eventPrefix = "",
}) => {
  const {
    value: isPasswordFieldFocused,
    setTrue: focusPasswordField,
    setFalse: blurPasswordField,
  } = useBoolean(false);

  const form = useFormContext<PasswordFormValue>();
  const password = form.watch("password");
  const inputClassName = useEmailLoginFormStyling ? "email-login-form-input" : undefined;

  return (
    <>
      <PasswordRequirementTooltip isOpen={isPasswordFieldFocused} password={password}>
        <FormField
          className="w-full"
          label={isNewPassword ? t("user_info.password") : t("user_info.new_password")}
          name="password"
          required
        >
          {(controlProps) => (
            <TextField
              {...form.register("password", {
                required: t("error.field_required"),
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: t("error.change_password.password_too_short", {
                    count: PASSWORD_MIN_LENGTH,
                  }),
                },
                validate: {
                  mustBeDifferent: (value: string) => {
                    if (value === currentPassword) {
                      return t("error.change_password.must_be_different");
                    }
                  },
                  mustHaveNumber: (value: string) => {
                    if (!hasNumber(value)) {
                      return t("error.change_password.no_digit");
                    }
                  },
                  mustHaveUppercase: (value: string) => {
                    if (!hasUppercase(value)) {
                      return t("error.change_password.no_uppercase");
                    }
                  },
                  mustHaveLowercase: (value: string) => {
                    if (!hasLowercase(value)) {
                      return t("error.change_password.no_lowercase");
                    }
                  },
                },
                onBlur: blurPasswordField,
              })}
              {...controlProps}
              autoComplete="new-password"
              className={inputClassName}
              eventName={`${eventPrefix}Password`}
              type="password"
              onFocus={focusPasswordField}
            />
          )}
        </FormField>
      </PasswordRequirementTooltip>

      <FormField
        label={
          isNewPassword ? t("user_info.confirm_password") : t("user_info.confirm_new_password")
        }
        name="confirm_password"
        required
      >
        {(controlProps) => (
          <TextField
            {...form.register("confirm_password", {
              required: t("error.field_required"),
              validate: {
                match: (value: string) => {
                  if (value !== password) {
                    return t("error.change_password.do_not_match");
                  }
                },
              },
            })}
            {...controlProps}
            autoComplete="new-password"
            className={inputClassName}
            eventName={`${eventPrefix}Confirm password`}
            type="password"
          />
        )}
      </FormField>
    </>
  );
};
